import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConnectionHealthChip from "../../components/ConnectionHealthChip";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import {
  KNOWN_STATUSES,
  KNOWN_CHANNELS,
  buildConnectionsFilterParams,
  formatApproxTime,
  formatConnectionUpdatedAt
} from "./superConnectionsUtils";

const PAGE_SIZE = 20;
const TABLE_COLUMN_COUNT = 9;

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  filtersPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const EMPTY_FILTERS = { companyId: "", status: "", channel: "", searchParam: "" };

const SuperConnectionsDashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { list: listCompanies } = useCompanies();

  // useCompanies() não retorna uma função com referência estável entre
  // renders (é recriada a cada chamada do hook). Guardamos a versão mais
  // recente em uma ref para poder chamá-la dentro do efeito sem precisar
  // listá-la como dependência — assim o efeito de carregamento das
  // empresas depende apenas de `authorized`.
  const listCompaniesRef = useRef(listCompanies);
  listCompaniesRef.current = listCompanies;

  // A verificação real de permissão é feita pelo backend em toda
  // requisição (middleware ensureSuperAdmin). Esta guarda é apenas
  // visual/UX — nunca a única barreira de segurança.
  const [authorized, setAuthorized] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(1);

  const [connections, setConnections] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!user?.super) {
      toast.error(i18n.t("superConnections.accessDenied"));
      history.push("/");
      return;
    }
    setAuthorized(true);
  }, [user, history]);

  useEffect(() => {
    if (!authorized) return;

    (async () => {
      try {
        const data = await listCompaniesRef.current();
        if (isMountedRef.current) {
          setCompanies(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (isMountedRef.current) toastError(err);
      }
    })();
  }, [authorized]);

  useEffect(() => {
    setPageNumber(1);
  }, [filters]);

  useEffect(() => {
    if (!authorized) return;

    setLoading(true);
    setLoadError(false);
    const currentRequestId = ++requestIdRef.current;

    const delayDebounceFn = setTimeout(() => {
      (async () => {
        try {
          const params = buildConnectionsFilterParams({ ...filters, pageNumber });
          const { data } = await api.get("/super/connections", { params });

          if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;

          setConnections(Array.isArray(data?.connections) ? data.connections : []);
          setCount(typeof data?.count === "number" ? data.count : 0);
          setLoading(false);
        } catch (err) {
          if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;
          toastError(err);
          setConnections([]);
          setCount(0);
          setLoading(false);
          setLoadError(true);
        }
      })();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [authorized, filters, pageNumber]);

  if (!authorized) return null;

  const handleFilterChange = field => event => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          {i18n.t("superConnections.title")} ({count})
        </Title>
      </MainHeader>

      <Paper className={classes.filtersPaper} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={i18n.t("superConnections.filters.searchPlaceholder")}
              value={filters.searchParam}
              onChange={handleFilterChange("searchParam")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{i18n.t("superConnections.filters.company")}</InputLabel>
              <Select
                value={filters.companyId}
                onChange={handleFilterChange("companyId")}
                label={i18n.t("superConnections.filters.company")}
              >
                <MenuItem value="">{i18n.t("superConnections.filters.all")}</MenuItem>
                {companies.map(company => (
                  <MenuItem key={company.id} value={String(company.id)}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{i18n.t("superConnections.filters.status")}</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange("status")}
                label={i18n.t("superConnections.filters.status")}
              >
                <MenuItem value="">{i18n.t("superConnections.filters.all")}</MenuItem>
                {KNOWN_STATUSES.map(status => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{i18n.t("superConnections.filters.channel")}</InputLabel>
              <Select
                value={filters.channel}
                onChange={handleFilterChange("channel")}
                label={i18n.t("superConnections.filters.channel")}
              >
                <MenuItem value="">{i18n.t("superConnections.filters.all")}</MenuItem>
                {KNOWN_CHANNELS.map(channel => (
                  <MenuItem key={channel} value={channel}>
                    {channel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button fullWidth onClick={handleClearFilters}>
              {i18n.t("superConnections.filters.clear")}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("superConnections.table.company")}</TableCell>
              <TableCell align="center">{i18n.t("superConnections.table.connection")}</TableCell>
              <TableCell align="center">{i18n.t("superConnections.table.channel")}</TableCell>
              <TableCell align="center">{i18n.t("superConnections.table.number")}</TableCell>
              <TableCell align="center">{i18n.t("superConnections.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("superConnections.table.isDefault")}</TableCell>
              <TableCell align="center">{i18n.t("superConnections.table.updatedAt")}</TableCell>
              <TableCell align="center">{i18n.t("superConnections.table.timeInStatus")}</TableCell>
              <TableCell align="center">{i18n.t("superConnections.table.health")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && !loadError && connections.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={TABLE_COLUMN_COUNT}>
                  {i18n.t("superConnections.table.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading && loadError && (
              <TableRow>
                <TableCell align="center" colSpan={TABLE_COLUMN_COUNT}>
                  {i18n.t("superConnections.table.error")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              !loadError &&
              connections.map(connection => (
                <TableRow key={connection.whatsappId}>
                  <TableCell align="center">{connection.companyName}</TableCell>
                  <TableCell align="center">{connection.name}</TableCell>
                  <TableCell align="center">{connection.channel}</TableCell>
                  <TableCell align="center">{connection.number || "-"}</TableCell>
                  <TableCell align="center">{connection.status || "-"}</TableCell>
                  <TableCell align="center">
                    {connection.isDefault
                      ? i18n.t("superConnections.table.yes")
                      : i18n.t("superConnections.table.no")}
                  </TableCell>
                  <TableCell align="center">
                    {formatConnectionUpdatedAt(connection.updatedAt) ||
                      i18n.t("superConnections.time.unknown")}
                  </TableCell>
                  <TableCell align="center">
                    {formatApproxTime(
                      connection.approxTimeInStatusSeconds,
                      (key, options) => i18n.t(key, options)
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <ConnectionHealthChip health={connection.health} />
                  </TableCell>
                </TableRow>
              ))}
            {loading && <TableRowSkeleton columns={TABLE_COLUMN_COUNT} />}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={count}
          page={pageNumber - 1}
          onChangePage={handleChangePage}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onChangeRowsPerPage={() => {}}
        />
      </Paper>
    </MainContainer>
  );
};

export default SuperConnectionsDashboard;
